import React, { useState, useEffect } from 'react';
import { diagnosisApi } from '../../services/api';
import { invalidateLookup } from '../../services/lookupCache';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiTrash, HiX, HiCheckCircle } from 'react-icons/hi';
import ConfirmDialog from '../../components/ConfirmDialog';

const DiagnosisCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: '',
    is_active: true
  });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const response = await diagnosisApi.getAll();
      setCodes(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch diagnosis codes');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCode(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      category: '',
      is_active: true
    });
    setShowForm(true);
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      name: code.name,
      description: code.description || '',
      category: code.category || '',
      is_active: code.is_active === 1 || code.is_active === true
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCode) {
        await diagnosisApi.update(editingCode.id, formData);
        toast.success('Diagnosis code updated successfully');
      } else {
        await diagnosisApi.create(formData);
        toast.success('Diagnosis code created successfully');
      }
      invalidateLookup('diagnosis:codes');
      setShowForm(false);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save diagnosis code');
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      onConfirm: async () => {
        try {
          await diagnosisApi.delete(id);
          invalidateLookup('diagnosis:codes');
          toast.success('Diagnosis code deleted successfully');
          fetchCodes();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete diagnosis code');
        }
      }
    });
  };

  const handleToggleActive = async (code) => {
    try {
      await diagnosisApi.update(code.id, {
        is_active: !(code.is_active === 1 || code.is_active === true)
      });
      invalidateLookup('diagnosis:codes');
      toast.success('Diagnosis code updated successfully');
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update diagnosis code');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  // Group codes by category
  const groupedCodes = codes.reduce((acc, code) => {
    const category = code.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(code);
    return acc;
  }, {});

  const categories = Object.keys(groupedCodes).sort();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Diagnosis Codes Configuration</h1>
          <p className="text-gray-500 mt-1">Manage diagnosis codes and categories</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <HiPlus className="w-5 h-5" />
          Add Diagnosis Code
        </button>
      </div>

      {/* Diagnosis Codes List */}
      <div className="card">
        {categories.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            No diagnosis codes found. Click "Add Diagnosis Code" to create one.
          </div>
        ) : (
          <div className="space-y-6">
            {categories.map((category) => (
              <div key={category}>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                  {category}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left p-4 font-semibold text-gray-700">Code</th>
                        <th className="text-left p-4 font-semibold text-gray-700">Name</th>
                        <th className="text-left p-4 font-semibold text-gray-700">Description</th>
                        <th className="text-center p-4 font-semibold text-gray-700">Status</th>
                        <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedCodes[category].map((code) => (
                        <tr key={code.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-4 font-mono font-medium text-gray-900">{code.code}</td>
                          <td className="p-4 font-medium text-gray-900">{code.name}</td>
                          <td className="p-4 text-gray-600">{code.description || '-'}</td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => handleToggleActive(code)}
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                (code.is_active === 1 || code.is_active === true)
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {(code.is_active === 1 || code.is_active === true) ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEdit(code)}
                                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                                title="Edit"
                              >
                                <HiPencil className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(code.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                title="Delete"
                              >
                                <HiTrash className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingCode ? 'Edit Diagnosis Code' : 'Add Diagnosis Code'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    placeholder="e.g., F84.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Autism Spectrum Disorders"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Autistic disorder"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Detailed description of the diagnosis code"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-900">Active</span>
                  <p className="text-xs text-gray-500 mt-1">Enable or disable this diagnosis code</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingCode ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, onConfirm: null })}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title="Delete Diagnosis Code"
        message="Are you sure you want to delete this diagnosis code?"
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default DiagnosisCodes;


